import { defineAsyncComponent } from 'vue';
import { defineStore } from 'pinia';
import { axiosLevelTech } from '@/services/axiosConfig';
import { useModalStore } from './modalStore';

// Define an interface for each menu item with optional and required properties
interface SiteMapEntry {
  img?: any; // Optional image URL for the menu item
  mobile?: boolean; // Optional flag indicating mobile-specific visibility
  pos?: string; // Optional position attribute
  title: string; // Required title of the menu item
  href: any; // General type for hyperlink, should be further defined
  highlight?: boolean; // Optional highlight attribute for important items
  style?: string; // Optional style descriptor
  playerid?: string; // Optional player ID for dynamic URLs
  text?: string; // Optional text content for the menu item
}

// Define an interface for the site map structure containing various menu items
interface SiteMap {
  [key: string]: SiteMapEntry | { [key: string]: SiteMapEntry }; // Dynamic keys with nested structure
}

// Define an interface for the overall menu data containing different categories of menu items
interface MenuData {
  site_map: SiteMap; // Structured map of site navigation elements
  top_menu_items: { [key: string]: SiteMapEntry }; // Top menu items collection
  left_menu_items: { [key: string]: SiteMapEntry }; // Left-side menu items collection
  bottom_menu_items: { [key: string]: SiteMapEntry }; // Bottom menu items collection
}

// Define an interface representing the overall state of user data in the store
interface UserDataState {
  menuData: MenuData; // Menu data structured according to MenuData interface
  notifications: any[]; // Array of notifications, type to be further defined as needed
  banners: any[]; // Array of banners, type to be further defined as needed
  jsdefs: any; // JavaScript definitions, type to be further defined as needed
  userInfo: UserInfo | null; // User information, type to be further defined as needed
  triggers: any[]; // Array of triggers, type to be further defined as needed
  classicFeatures: UserInfo['classicFeatures'] | null; // Array of classic features, type to be further defined as needed
  currentAppVersion: string; // Current app version, used for update checks
}

// User info interface matching the structure expected in the state
export interface UserInfo {
  email: string; // The email will be fetched from userName in JwtPayload
  personId: string;
  playerid: number;
  membership_tiers: {
    is_visitor: boolean;
    is_basic: boolean;
    is_generic: boolean;
    is_ngb: boolean;
    is_trial: boolean;
    is_gold: boolean;
    is_platinum: boolean;
    is_vip: boolean;
    is_developer: boolean;
    is_admin: boolean;
    is_club_admin: boolean;
    is_county_admin: boolean;
    is_results_admin: boolean;
    is_premium: boolean;
  };
  classicFeatures: {
    player_details: boolean;
    player_rankings: boolean;
    world_ranking: boolean;
    country_ranking: boolean;
    player_level: boolean;
    my_profile: boolean;
    match_hist: boolean;
    dashboard: boolean;
    local_communities: boolean;
    notifications: boolean;
    see_posts: boolean;
    comments: boolean;
    basic_stats: boolean;
    follow_15: boolean;
    email_matches: boolean;
    skills_hist_1: boolean;
    self_edit: boolean;
    add_club_result: boolean;
    potm: boolean;
    potw: boolean;
    event_adverts: boolean;
    duplicates: boolean;
    source_links: boolean;
    country_focus: boolean;
    NGB_communities: boolean;
    NGB_nos: boolean;
    basic_chart: boolean;
    graphical_stats: boolean;
    edit_club_details: boolean;
    club_ranking: boolean;
    community_ranking: boolean;
    rankings_filter: boolean;
    match_hist_3: boolean;
    other_hist_1: boolean;
    skills_hist_3: boolean;
    follow_20: boolean;
    explain_levels: boolean;
    view_club_details: boolean;
    add_club_results: boolean;
    match_hist_5y: boolean;
    skills_hist_chart: boolean;
    follow_all: boolean;
    post: boolean;
    reduced_ads: boolean;
    add_any_result: boolean;
    adv_charts: boolean;
    adv_stats: boolean;
    expected_result: boolean;
    h2h: boolean;
    email_match_details: boolean;
    most_improved: boolean;
    config_views: boolean;
    favourites: boolean;
    match_hist_all: boolean;
    psa_hist_all: boolean;
    what_if: boolean;
    match_review: boolean;
    no_ads: boolean;
    add_community: boolean;
    early_access: boolean;
    support_sl: boolean;
    squashskills: boolean;
  };
}

// Define the store using the 'defineStore' method from Pinia, providing the store ID and its structure
export const useUserDataStore = defineStore('userData', {
  // Define the initial state using a function that returns the state structure
  state: (): UserDataState => ({
    menuData: {
      site_map: {},
      top_menu_items: {},
      left_menu_items: {},
      bottom_menu_items: {},
    },
    notifications: [],
    banners: [],
    jsdefs: {},
    userInfo: null,
    triggers: [],
    classicFeatures: null,
    currentAppVersion: '', // Added to store the current app version
  }),
  
  // Actions contain methods for modifying the state
  actions: {
    // Asynchronous action to fetch user data from an API endpoint
    async fetchUserData() {
      try {
        const response = await axiosLevelTech.get('/api/app/Initialise');
        this.menuData = response.data.data.Menu;
        this.notifications = response.data.data.Notifications;
        this.banners = response.data.data.banners;
        this.jsdefs = response.data.data.JsDefs;
        this.userInfo = {
          ...response.data.data.UserInfo,
          personId: response.data.data.UserInfo.personid || response.data.data.UserInfo.personId, // Normalize personId
        };
        this.triggers = response.data.data.Triggers;
        this.classicFeatures = response.data.data.ClassicFeatures;
        this.currentAppVersion = response.data.data.CurrentAppVersion ?? ''; // Store the current app version
        // Grouped logging for personId
        if (this.userInfo && this.userInfo.personId) {
          // Use normalized 'personId'
        } else {
          console.warn('[API Initialise] No Person ID found in UserInfo.');
        }
      } catch (error) {
        console.error('Error Fetching User Data:', error);
      } finally {
      }
    },
    // Action to mark all notifications as read
    async markNotificationsAsRead() {
      try {
        const response = await axiosLevelTech.post('/api/notification/MarkAsRead');
        
        if (response.data.status.toLowerCase() === 'success') {
          const updatedNotifications = this.notifications.map((notification) => {
            if (!notification.read) {
              return { ...notification, read: true };
            }
            return notification;
          });
          
          this.notifications = [...updatedNotifications]; // Trigger reactivity
        } else {
          console.error('Failed to mark notifications as read:', response.data.message);
        }
      } catch (error) {
        console.error('Error calling MarkAsRead API:', error);
      }
    },
    async checkForTriggers() {
      if (this.triggers) {
        if (this.triggers.length > 0 && this.triggers[0].id) {
        } else {
          console.warn('No valid Trigger ID found.');
        }
      } else {
        console.warn('No Triggers data found in response.');
      }
      
      // Re-Active Pop-up Logic by changing 0 to 1
      const activeTrigger = this.triggers.find((trigger) => trigger.active === 1);
      if (activeTrigger) {
        const triggerData = JSON.parse(activeTrigger.trigger_json);
        const modalStore = useModalStore();
        
        setTimeout(() => {
          modalStore.openModal({
            title: 'help us improve our accuracy!',
            description: 'By reviewing your last match result',
            component: defineAsyncComponent(() => import('@/components/AdminComponents/Questionnaires/QuizComponent.vue')),
            props: {},
          });
        }, 5000); // 5 seconds delay on the popup
      }
    }
  },
  
  getters: {
    personid(): string {
      return this.userInfo ? this.userInfo.personId : "";
    },
    hasAccess(): (allowedRoles: (keyof UserInfo['membership_tiers'])[]) => boolean {
      return (allowedRoles: (keyof UserInfo['membership_tiers'])[]) => {
        if (!this.userInfo || !this.userInfo.membership_tiers) {
          return false;
        }
        
        // Special case: if no roles passed, allow access to everyone
        if (allowedRoles.length === 0) {
          return true;
        }
        
        const roleCheck = (role: keyof UserInfo['membership_tiers']) =>
          this.userInfo!.membership_tiers[role];
        
        return allowedRoles.some(roleCheck);
      };
    },
    featureAvailable(state: UserDataState): (feature: keyof UserInfo['classicFeatures']) => boolean {
      return (feature: keyof UserInfo['classicFeatures']) => {
        if (!state.classicFeatures) {
          return false;
        }
        return state.classicFeatures[feature] === true;
      };
    }
  },
});
